// @tennis-matches/services/governance
// Globale Registrierungs-/Neu-Verein-Onboarding-Queue (nur Superadmin listet/entscheidet)

import { getSupabaseClient } from "../supabase/index.js";
import {
  APP_CONFIG_SCHEMA_REST_KEY,
  isAppConfigSchemaUnavailableError,
  isMissingRestResourceError,
  isRestEndpointUnavailable,
  markRestEndpointUnavailable,
} from "../supabase/rest-endpoint-availability.js";

const SCHEMA = "app_config";
const MY_PENDING_RPC_KEY = "rpc:get_my_pending_registration_onboarding_request";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __db(supabase) {
  return supabase && typeof supabase.schema === "function" ? supabase.schema(SCHEMA) : supabase;
}

function __supabaseErrorInfo(error) {
  if (!error) return null;
  return {
    message: __asStr(error?.message),
    code: __asStr(error?.code),
    details: __asStr(error?.details),
    hint: __asStr(error?.hint),
    status: Number(error?.status || 0) || null,
  };
}

function __isMissingTableError(error) {
  return isMissingRestResourceError(error) || isAppConfigSchemaUnavailableError(error);
}

function __normalizePendingRow(row) {
  if (!row || typeof row !== "object") return null;
  return {
    id: row.id,
    status: row.status,
    requested_club_name: row.requested_club_name,
    requested_club_city: row.requested_club_city,
    requested_roles: Array.isArray(row.requested_roles) ? row.requested_roles : [],
    requested_apps: row.requested_apps,
    created_at: row.created_at,
    request_note: row.request_note,
  };
}

async function __loadMyPendingRegistrationOnboardingViaPublicRpc(supabase) {
  if (isRestEndpointUnavailable(MY_PENDING_RPC_KEY)) {
    return { kind: "skip" };
  }

  const { data, error } = await supabase.rpc("get_my_pending_registration_onboarding_request");
  if (!error) {
    const payload = data && typeof data === "object" ? data : {};
    return { kind: "ok", row: __normalizePendingRow(payload.row) };
  }

  if (isMissingRestResourceError(error)) {
    markRestEndpointUnavailable(MY_PENDING_RPC_KEY);
    return { kind: "skip" };
  }

  return { kind: "error", error };
}

async function __loadMyPendingRegistrationOnboardingViaSchemaTable(supabase) {
  if (isRestEndpointUnavailable(APP_CONFIG_SCHEMA_REST_KEY)) {
    return { kind: "missing" };
  }

  const { data: authData, error: authErr } = await supabase.auth.getUser();
  if (authErr || !authData?.user?.id) return { kind: "error", error: authErr };

  const db = __db(supabase);
  const uid = __asStr(authData.user.id);

  const { data, error } = await db
    .from(T_REG_ONBOARD)
    .select(
      "id,status,requested_club_name,requested_club_city,requested_roles,requested_apps,created_at,request_note"
    )
    .eq("requester_user_id", uid)
    .eq("status", "pending")
    .maybeSingle();

  if (error) {
    if (__isMissingTableError(error)) {
      markRestEndpointUnavailable(APP_CONFIG_SCHEMA_REST_KEY);
      return { kind: "missing" };
    }
    return { kind: "error", error };
  }

  return { kind: "ok", row: __normalizePendingRow(data) };
}

/** Ergebnis-Kennzeichen für applyPendingSignupAccessRequestRemote */
export const REGISTRATION_ONBOARDING_OUTCOME = "registration_onboarding";

const T_REG_ONBOARD = "registration_onboarding_requests";

/**
 * Eigene ausstehende Neu-Verein-/Registrierungsanfrage (RLS: requester = auth.uid()).
 * @returns {Promise<{ ok: boolean, row?: any|null, missing?: boolean, reason?: string, error?: unknown }>}
 */
export async function loadMyPendingRegistrationOnboardingRemote() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase", row: null };

  const rpcRes = await __loadMyPendingRegistrationOnboardingViaPublicRpc(supabase);
  if (rpcRes.kind === "ok") {
    return { ok: true, row: rpcRes.row };
  }
  if (rpcRes.kind === "error") {
    return { ok: false, reason: "rpc-error", error: rpcRes.error, row: null };
  }

  const tableRes = await __loadMyPendingRegistrationOnboardingViaSchemaTable(supabase);
  if (tableRes.kind === "ok") {
    return { ok: true, row: tableRes.row };
  }
  if (tableRes.kind === "missing") {
    return { ok: true, row: null, missing: true };
  }

  return { ok: false, reason: "db-error", error: tableRes.error, row: null };
}

/**
 * Neu-Verein-Anfrage (Marketplace / nach Registrierung) — globale Superadmin-Queue.
 * @returns {Promise<{ ok: boolean, result?: any, reason?: string, error?: unknown, errorInfo?: any }>}
 */
export async function createRegistrationOnboardingRequestRemote({
  requestedClubName,
  requestedClubCity,
  requestedRoles,
  requestedApps = null,
  requestedCapabilities = {},
  requestNote = null,
} = {}) {
  const clubName = __asStr(requestedClubName);
  const clubCity = __asStr(requestedClubCity);
  const roles = Array.isArray(requestedRoles) ? requestedRoles.map(__asStr).filter(Boolean) : [];
  const caps = requestedCapabilities && typeof requestedCapabilities === "object" ? requestedCapabilities : {};
  const appsRaw = Array.isArray(requestedApps) ? requestedApps.map(__asStr).filter(Boolean) : null;

  if (!clubName) return { ok: false, reason: "missing-club-name" };
  if (!roles.length) return { ok: false, reason: "missing-roles" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc("create_registration_onboarding_request", {
    p_requested_club_name: clubName,
    p_requested_club_city: clubCity || null,
    p_requested_roles: roles,
    p_requested_apps: appsRaw?.length ? appsRaw : null,
    p_requested_capabilities: caps,
    p_request_note: requestNote == null ? null : __asStr(requestNote) || null,
  });

  if (error) {
    return { ok: false, reason: "rpc-error", error, errorInfo: __supabaseErrorInfo(error) };
  }
  return { ok: true, result: data || null };
}

/**
 * @returns {Promise<{ ok: boolean, requests?: any[], reason?: string, error?: any, errorInfo?: any }>}
 */
export async function loadGlobalRegistrationOnboardingPendingRemote() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc("list_global_onboarding_requests");

  if (error) {
    return { ok: false, reason: "rpc-error", error, errorInfo: __supabaseErrorInfo(error) };
  }

  const payload = data && typeof data === "object" ? data : {};
  const raw = payload.requests;
  let requests = [];
  if (Array.isArray(raw)) {
    requests = raw;
  } else if (raw && typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      requests = Array.isArray(parsed) ? parsed : [];
    } catch {
      requests = [];
    }
  }

  return { ok: Boolean(payload.ok !== false), requests };
}

/**
 * @param {string} requestId
 * @param {string[]} finalRoles
 * @param {string|null} decisionNote
 */
export async function approveGlobalRegistrationOnboardingRemote(
  requestId,
  finalRoles,
  decisionNote = null,
  targetScopeId = null,
  correctedClubName = null,
  correctedClubCity = null
) {
  const rid = __asStr(requestId);
  const roles = Array.isArray(finalRoles) ? finalRoles.map(__asStr).filter(Boolean) : [];
  if (!rid || !roles.length) return { ok: false, reason: "missing-args" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const ts = targetScopeId != null ? __asStr(targetScopeId) : "";
  const cn = correctedClubName != null ? __asStr(correctedClubName) : "";
  const cc = correctedClubCity != null ? __asStr(correctedClubCity) : "";

  const { data, error } = await supabase.rpc("approve_global_onboarding_request", {
    p_request_id: rid,
    p_final_roles: roles,
    p_decision_note: decisionNote ?? null,
    p_target_scope_id: ts ? ts : null,
    p_corrected_club_name: cn ? cn : null,
    p_corrected_club_city: cc ? cc : null,
  });

  if (error) return { ok: false, reason: "rpc-error", error, errorInfo: __supabaseErrorInfo(error) };
  return { ok: true, result: data || null };
}

/**
 * @param {string} requestId
 * @param {string|null} decisionNote
 */
export async function rejectGlobalRegistrationOnboardingRemote(requestId, decisionNote = null) {
  const rid = __asStr(requestId);
  if (!rid) return { ok: false, reason: "missing-id" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc("reject_global_onboarding_request", {
    p_request_id: rid,
    p_decision_note: decisionNote ?? null,
  });

  if (error) return { ok: false, reason: "rpc-error", error, errorInfo: __supabaseErrorInfo(error) };
  return { ok: true, result: data || null };
}
